import { z } from 'zod';

/**
 * Mowa na tekst (Fala 10, DECISIONS 2026-10-07 [S] „Copilot ma być prawdziwym asystentem”, tor bubble-voice): dyktowanie
 * do czatu Copilota i do polecenia głosem. Dwa silniki:
 *  - `browser` — Web Speech API w przeglądarce (pl-PL, Chrome; bez kosztu w Fravity OS, do aplikacji trafia tylko tekst),
 *  - `openai` — nagranie (MediaRecorder) → `POST /api/voice/transcribe` → adapter transkrypcji OpenAI z kluczem
 *    z integracji AI; nagranie tylko w pamięci serwera, usuwane po transkrypcji (nigdy na dysku ani w bazie).
 * `auto` = przeglądarka, a gdy jej brak (Firefox, część Safari) albo usługa mowy przeglądarki zawiedzie — OpenAI.
 *
 * Ustawienie workspace'u (Ustawienia → AI i pamięć → Model i limity → „Mowa na tekst”) jest w `ai_settings.profiles.speech`
 * (kształt JSON, bez migracji — jak poziom rozumowania w Fali 9). Model transkrypcji i cena za minutę z konfiguracji,
 * nie z kodu.
 */

export const SPEECH_ENGINES = ['auto', 'browser', 'openai'] as const;
export type SpeechEngine = (typeof SPEECH_ENGINES)[number];

/** Najdłuższe nagranie do transkrypcji na serwerze (sekundy) — przeglądarka kończy nagrywanie sama. */
export const VOICE_TRANSCRIBE_MAX_SECONDS = 120;
/** Największe nagranie (bajty): 2 min Opus/AAC mieści się z dużym zapasem (OpenAI przyjmuje do 25 MB). */
export const VOICE_TRANSCRIBE_MAX_BYTES = 5 * 1024 * 1024;
/** Najkrótsze nagranie (bajty) — pusty plik = „nic nie usłyszałem” bez wywołania dostawcy. */
export const VOICE_TRANSCRIBE_MIN_BYTES = 200;
/** Limit wywołań transkrypcji na osobę i adres (okno 10 min) — koszt i nadużycia. */
export const VOICE_TRANSCRIBE_RATE_LIMIT = { max: 30, windowMinutes: 10 } as const;

/**
 * Typy nagrań przyjmowane przez endpoint (bez parametrów, np. `audio/webm;codecs=opus` → `audio/webm`). Tylko formaty
 * z dokumentacji OpenAI (8.10.2026: mp3, mp4, mpeg, mpga, m4a, wav, webm — bez ogg), żeby nieobsługiwany plik był
 * odrzucony od razu z czytelnym komunikatem, a nie przez dostawcę.
 */
export const VOICE_AUDIO_MIME_TYPES = ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/wav', 'audio/x-wav'] as const;

/** Nagłówek z długością nagrania w ms (mierzy przeglądarka; serwer odrzuca dłuższe niż limit). */
export const VOICE_DURATION_HEADER = 'x-fos-audio-duration-ms';

/** `audio/webm;codecs=opus` → `audio/webm`; nieobsługiwany typ = null. */
export function normalizeAudioMime(value: string | null | undefined): (typeof VOICE_AUDIO_MIME_TYPES)[number] | null {
  const base = (value ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  return (VOICE_AUDIO_MIME_TYPES as readonly string[]).includes(base)
    ? (base as (typeof VOICE_AUDIO_MIME_TYPES)[number])
    : null;
}

const pricePerMinuteSchema = z
  .string()
  .trim()
  .regex(/^\d{1,6}(\.\d{1,4})?$/, { message: 'Nieprawidłowa kwota (np. 0,0175)' });

/** Identyfikator modelu z konfiguracji (np. `gpt-transcribe`) — bez wartości w kodzie. */
const speechModelSchema = z
  .string()
  .trim()
  .min(1, { message: 'Podaj model transkrypcji' })
  .max(100, { message: 'Najwyżej 100 znaków' })
  .regex(/^[A-Za-z0-9._:-]+$/, { message: 'Tylko litery, cyfry, kropka, myślnik i podkreślenie' });

/** `ai_settings.profiles.speech`. */
export const speechSettingsSchema = z.object({
  engine: z.enum(SPEECH_ENGINES),
  /** model transkrypcji OpenAI; null = transkrypcja na serwerze wyłączona (tylko przeglądarka) */
  model: speechModelSchema.nullable(),
  /** cena w PLN za minutę nagrania (4 miejsca); null = brak ceny (przy budżecie AI transkrypcja jest odrzucana) */
  pricePerMinutePln: pricePerMinuteSchema.nullable(),
});
export type SpeechSettings = z.infer<typeof speechSettingsSchema>;

export const SPEECH_DEFAULTS: SpeechSettings = { engine: 'auto', model: null, pricePerMinutePln: null };

/** Zapis ustawień mowy (owner / admin / manager; cena tylko z „Widzi stawki i kwoty”). */
export const updateSpeechSettingsInput = z
  .object({
    engine: z.enum(SPEECH_ENGINES),
    model: speechModelSchema.nullable(),
    /** pominięte = bez zmiany (osoba bez „Widzi stawki i kwoty” nie zmienia ceny) */
    pricePerMinutePln: pricePerMinuteSchema.nullable().optional(),
  })
  .strict();
export type UpdateSpeechSettingsInput = z.input<typeof updateSpeechSettingsInput>;

/**
 * Dlaczego transkrypcja na serwerze jest niedostępna: AI wyłączone, silnik „tylko przeglądarka”, brak klucza OpenAI,
 * brak modelu, brak ceny przy budżecie, klucz odrzucony / błąd integracji, brak ustawień AI, AI osoby wyłączone (limit AI
 * osoby 0 zł — partner korzysta z AI we własnym workspace'ie).
 */
export const SPEECH_SERVER_REASONS = [
  'disabled',
  'browser_only',
  'no_key',
  'no_model',
  'no_price',
  'provider_error',
  'member_disabled',
] as const;
export type SpeechServerReason = (typeof SPEECH_SERVER_REASONS)[number];

/** `voice.speechStatus` — co może mikrofon w tym workspace (bez kluczy i kosztów). */
export interface SpeechStatusView {
  engine: SpeechEngine;
  server: {
    available: boolean;
    /** symulacja (atrapa transkrypcji, `TRANSCRIPTION_PROVIDER=fake` poza produkcją) */
    simulated: boolean;
    reason: SpeechServerReason | null;
    message: string | null;
  };
  limits: { maxSeconds: number; maxBytes: number };
}

/** `voice.speechSettings` — formularz w Ustawieniach (owner / admin / manager). */
export interface SpeechSettingsView {
  settings: SpeechSettings;
  /** cena ukryta bez „Widzi stawki i kwoty” */
  showMoney: boolean;
  canEdit: boolean;
  /** wersja wiersza `ai_settings`; null = workspace bez zapisanych ustawień AI */
  version: number | null;
  status: SpeechStatusView;
}

/** Wynik `POST /api/voice/transcribe`. */
export interface TranscribeResult {
  text: string;
  simulated: boolean;
  durationSeconds: number | null;
}

export const SPEECH_ENGINE_LABELS: Record<SpeechEngine, string> = {
  auto: 'Automatycznie',
  browser: 'Przeglądarka',
  openai: 'OpenAI',
};

export const SPEECH_ENGINE_HINTS: Record<SpeechEngine, string> = {
  auto: 'Rozpoznawanie w przeglądarce (Chrome), a gdy go brak albo zawiedzie — transkrypcja przez OpenAI.',
  browser: 'Tylko rozpoznawanie mowy w przeglądarce (Chrome, pl-PL). Bez kosztów AI.',
  openai: 'Nagranie trafia do OpenAI (model z ustawień), tekst wraca do pola. Koszt liczony do budżetu AI.',
};

export const SPEECH_MESSAGES = {
  disabled: 'AI jest wyłączone — transkrypcja przez OpenAI nie działa. Mów w Chrome albo wpisz tekst.',
  browserOnly: 'W tym workspace mowę rozpoznaje tylko przeglądarka (Ustawienia → AI i pamięć → Mowa na tekst).',
  noKey: 'Brak klucza OpenAI — transkrypcja na serwerze nie działa. Mów w Chrome albo wpisz tekst.',
  noModel: 'Nie ustawiono modelu transkrypcji (Ustawienia → AI i pamięć → Mowa na tekst). Działa mowa w przeglądarce.',
  noPrice:
    'Model transkrypcji nie ma ceny za minutę, a budżet AI jest ustawiony — uzupełnij cenę w Ustawieniach → AI i pamięć.',
  noSettings: 'Najpierw zapisz ustawienia „Model i limity”, potem ustawienia mowy.',
  tooLong: `Nagranie jest za długie (najwyżej ${VOICE_TRANSCRIBE_MAX_SECONDS / 60} min). Nagraj krótsze polecenie.`,
  tooLarge: 'Nagranie jest za duże. Nagraj krótsze polecenie.',
  empty: 'Nic nie usłyszałem. Spróbuj jeszcze raz albo wpisz tekst.',
  unsupportedFormat: 'Nieobsługiwany format nagrania.',
  notStored: 'Nagranie nie jest zapisywane.',
} as const;
